/**
 * WebRTC Service - AUDIO ONLY
 * 
 * Handles peer-to-peer AUDIO calls without video.
 * Used for the "call" step in ConnectieFlow.
 * 
 * CRITICAL: This service MUST completely release the microphone
 * when endCall() is called, so Google Meet can use it later.
 */

export interface WebRTCCall {
  id: string;
  type: 'audio';  // Always audio, never video
  participants: string[];
  status: 'initiating' | 'connecting' | 'connected' | 'ended';
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  userName?: string;
}

interface CallOptions {
  audio: boolean;
  iceServers?: RTCIceServer[];
}

class WebRTCService {
  private localStream: MediaStream | null = null;
  private peerConnection: RTCPeerConnection | null = null;
  private currentCall: WebRTCCall | null = null;
  private remoteAudioElement: HTMLAudioElement | null = null;
  private callHandlers: {
    onCallStart?: (call: WebRTCCall) => void;
    onCallEnd?: (call: WebRTCCall) => void;
    onCallError?: (error: Error) => void;
    onRemoteStream?: (stream: MediaStream) => void;
    onIceCandidate?: (candidate: RTCIceCandidate) => void;
  } = {};

  private defaultIceServers: RTCIceServer[] = [];

  constructor() {
    this.setupDefaultIceServers();
    this.setupRemoteAudioElement();
  }

  /**
   * Setup hidden audio element for playing remote audio
   * CRITICAL: This is needed to actually HEAR the other person
   */
  private setupRemoteAudioElement(): void {
    if (typeof window === 'undefined') return;

    // Create hidden audio element
    this.remoteAudioElement = document.createElement('audio');
    this.remoteAudioElement.id = 'webrtc-remote-audio';
    this.remoteAudioElement.autoplay = true;
    this.remoteAudioElement.volume = 1.0; // MAX volume
    this.remoteAudioElement.muted = false;
    this.remoteAudioElement.style.display = 'none';
    
    // Add to body
    document.body.appendChild(this.remoteAudioElement);

    console.log('✅ [WebRTC] Remote audio element created and added to DOM');
    console.log('🔊 [WebRTC] Audio element ID:', this.remoteAudioElement.id);
    console.log('🔊 [WebRTC] Audio element autoplay:', this.remoteAudioElement.autoplay);
    console.log('🔊 [WebRTC] Audio element volume:', this.remoteAudioElement.volume);
    console.log('🔊 [WebRTC] Audio element muted:', this.remoteAudioElement.muted);
    
    // Log when audio starts playing
    this.remoteAudioElement.onplaying = () => {
      console.log('🎵 [WebRTC] Remote audio is NOW PLAYING!');
    };
    
    this.remoteAudioElement.onpause = () => {
      console.log('⏸️ [WebRTC] Remote audio PAUSED');
    };
    
    this.remoteAudioElement.onerror = (e) => {
      console.error('❌ [WebRTC] Audio element ERROR:', e);
    };
  }

  private setupDefaultIceServers() {
    // Default STUN servers for NAT traversal
    this.defaultIceServers = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' }
    ];
  }

  // ==========================================
  // AUDIO-ONLY CALL METHODS
  // ==========================================

  /**
   * Initialize an AUDIO-ONLY call
   * NO video support - video is handled by Google Meet
   */
  async initiateCall(
    participants: string[],
    options: CallOptions = { audio: true }
  ): Promise<WebRTCCall> {
    try {
      console.log('🎤 [WebRTC] Initiating AUDIO-ONLY call...');

      // FORCE audio-only (no video ever)
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false  // NEVER request video
      });

      console.log('✅ [WebRTC] Microphone permission granted');

      // Create peer connection
      this.peerConnection = new RTCPeerConnection({
        iceServers: options.iceServers || this.defaultIceServers
      });

      // Add local audio tracks to peer connection
      this.localStream.getTracks().forEach(track => {
        console.log('🎤 [WebRTC] Adding track:', track.kind, track.label);
        this.peerConnection?.addTrack(track, this.localStream!);
      });

      // Set up event handlers
      this.setupPeerConnectionHandlers();

      // Create call object
      this.currentCall = {
        id: `call-${Date.now()}`,
        type: 'audio',
        participants,
        status: 'initiating',
        startTime: new Date()
      };

      this.callHandlers.onCallStart?.(this.currentCall);

      return this.currentCall;

    } catch (error) {
      console.error('❌ [WebRTC] Error initiating call:', error);
      this.callHandlers.onCallError?.(error as Error);
      throw error;
    }
  }

  /**
   * Join existing AUDIO-ONLY call
   */
  async joinCall(
    callId: string,
    options: CallOptions = { audio: true }
  ): Promise<WebRTCCall> {
    try {
      console.log('🎤 [WebRTC] Joining AUDIO-ONLY call:', callId);

      // FORCE audio-only
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false  // NEVER request video
      });

      console.log('✅ [WebRTC] Microphone permission granted');

      // Create peer connection
      this.peerConnection = new RTCPeerConnection({
        iceServers: options.iceServers || this.defaultIceServers
      });

      // Add local audio tracks
      this.localStream.getTracks().forEach(track => {
        console.log('🎤 [WebRTC] Adding track:', track.kind);
        this.peerConnection?.addTrack(track, this.localStream!);
      });

      // Set up event handlers
      this.setupPeerConnectionHandlers();

      // Create call object
      this.currentCall = {
        id: callId,
        type: 'audio',
        participants: [],
        status: 'connecting',
        startTime: new Date()
      };

      return this.currentCall;

    } catch (error) {
      console.error('❌ [WebRTC] Error joining call:', error);
      this.callHandlers.onCallError?.(error as Error);
      throw error;
    }
  }

  // ==========================================
  // CLEANUP METHODS (CRITICAL FOR DEVICE RELEASE)
  // ==========================================

  /**
   * Stop ALL media tracks
   * MUST be called before opening Google Meet to release microphone
   */
  private stopAllMedia(): void {
    console.log('🛑 [WebRTC] Stopping all media tracks...');
    
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        console.log(`🛑 [WebRTC] Stopping track: ${track.kind} - ${track.label}`);
        track.stop();
      });
      this.localStream = null;
      console.log('✅ [WebRTC] All tracks stopped, microphone released');
    }

    // Stop remote audio playback
    if (this.remoteAudioElement) {
      this.remoteAudioElement.pause();
      this.remoteAudioElement.srcObject = null;
      console.log('✅ [WebRTC] Remote audio stopped');
    }
  }

  /**
   * Close peer connection
   */
  private closePeerConnection(): void {
    console.log('🛑 [WebRTC] Closing peer connection...');
    
    if (this.peerConnection) {
      // Remove all event handlers
      this.peerConnection.ontrack = null;
      this.peerConnection.onicecandidate = null;
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.oniceconnectionstatechange = null;

      // Close the connection
      this.peerConnection.close();
      this.peerConnection = null;
      console.log('✅ [WebRTC] Peer connection closed');
    }
  }

  /**
   * End current call and FULLY release all resources
   * Call this BEFORE starting Google Meet video call
   */
  endCall(): void {
    console.log('📞 [WebRTC] Ending call...');

    if (this.currentCall) {
      this.currentCall.status = 'ended';
      this.currentCall.endTime = new Date();
      if (this.currentCall.startTime) {
        this.currentCall.duration = 
          this.currentCall.endTime.getTime() - this.currentCall.startTime.getTime();
      }

      this.callHandlers.onCallEnd?.(this.currentCall);
    }

    // CRITICAL: Stop all tracks FIRST
    this.stopAllMedia();

    // THEN close peer connection
    this.closePeerConnection();

    // Clear current call
    this.currentCall = null;

    console.log('✅ [WebRTC] Call ended, all resources released');
  }

  // ==========================================
  // GETTERS
  // ==========================================

  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  getCurrentCall(): WebRTCCall | null {
    return this.currentCall;
  }

  isInCall(): boolean {
    return this.currentCall !== null && this.currentCall.status !== 'ended';
  }

  // ==========================================
  // SIGNALING METHODS
  // ==========================================

  setCallHandlers(handlers: typeof this.callHandlers): void {
    this.callHandlers = { ...this.callHandlers, ...handlers };
  }

  async createOffer(): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) {
      throw new Error('No active peer connection');
    }

    const offer = await this.peerConnection.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false  // NEVER receive video
    });
    await this.peerConnection.setLocalDescription(offer);
    console.log('✅ [WebRTC] Offer created');
    return offer;
  }

  async createAnswer(offer: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) {
      throw new Error('No active peer connection');
    }

    await this.peerConnection.setRemoteDescription(offer);
    const answer = await this.peerConnection.createAnswer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false  // NEVER receive video
    });
    await this.peerConnection.setLocalDescription(answer);
    console.log('✅ [WebRTC] Answer created');
    return answer;
  }

  async setRemoteDescription(description: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) {
      throw new Error('No active peer connection');
    }

    await this.peerConnection.setRemoteDescription(description);
    console.log('✅ [WebRTC] Remote description set');
  }

  async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.peerConnection) {
      throw new Error('No active peer connection');
    }

    await this.peerConnection.addIceCandidate(candidate);
  }

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  private setupPeerConnectionHandlers(): void {
    if (!this.peerConnection) return;

    // Handle remote stream (audio only)
    this.peerConnection.ontrack = (event) => {
      console.log('🎤 [WebRTC] Remote track received:', event.track.kind);
      console.log('🎤 [WebRTC] Track readyState:', event.track.readyState);
      console.log('🎤 [WebRTC] Track enabled:', event.track.enabled);
      
      const remoteStream = event.streams[0];
      if (remoteStream) {
        console.log('🎤 [WebRTC] Remote stream has', remoteStream.getTracks().length, 'tracks');
        
        // CRITICAL: Play remote audio through audio element
        if (this.remoteAudioElement) {
          this.remoteAudioElement.srcObject = remoteStream;
          this.remoteAudioElement.volume = 1.0; // MAX volume
          console.log('✅ [WebRTC] Remote audio connected to audio element');
          console.log('🔊 [WebRTC] Audio element volume:', this.remoteAudioElement.volume);
          console.log('🔊 [WebRTC] Audio element muted:', this.remoteAudioElement.muted);
          
          // Try to play (may be blocked by browser autoplay policy)
          this.remoteAudioElement.play()
            .then(() => {
              console.log('✅ [WebRTC] Remote audio is PLAYING!');
            })
            .catch(err => {
              console.error('❌ [WebRTC] Autoplay blocked! User interaction needed:', err);
              // Try to enable audio on user interaction
              document.addEventListener('click', () => {
                if (this.remoteAudioElement) {
                  this.remoteAudioElement.play()
                    .then(() => console.log('✅ [WebRTC] Audio started after user click'))
                    .catch(e => console.error('❌ [WebRTC] Still blocked:', e));
                }
              }, { once: true });
            });
        } else {
          console.error('❌ [WebRTC] No remote audio element available!');
        }

        // Still call the handler for UI updates
        this.callHandlers.onRemoteStream?.(remoteStream);
      } else {
        console.error('❌ [WebRTC] No remote stream in ontrack event!');
      }
    };

    // Handle ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('🧊 [WebRTC] ICE candidate generated');
        this.callHandlers.onIceCandidate?.(event.candidate);
      }
    };

    // Handle connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      if (this.peerConnection) {
        console.log('🔄 [WebRTC] Connection state:', this.peerConnection.connectionState);
        
        if (this.peerConnection.connectionState === 'connected' && this.currentCall) {
          this.currentCall.status = 'connected';
          console.log('✅ [WebRTC] Call connected!');
        }

        if (this.peerConnection.connectionState === 'failed' && this.currentCall) {
          console.error('❌ [WebRTC] Connection failed');
          this.callHandlers.onCallError?.(new Error('Connection failed'));
        }
      }
    };

    // Handle ICE connection state
    this.peerConnection.oniceconnectionstatechange = () => {
      if (this.peerConnection) {
        console.log('🧊 [WebRTC] ICE connection state:', this.peerConnection.iceConnectionState);
      }
    };
  }

  // ==========================================
  // DEBUG HELPERS
  // ==========================================

  /**
   * Get debug info about current call state
   */
  getDebugInfo(): any {
    return {
      hasLocalStream: !!this.localStream,
      hasPeerConnection: !!this.peerConnection,
      hasRemoteAudioElement: !!this.remoteAudioElement,
      currentCall: this.currentCall,
      localStreamTracks: this.localStream?.getTracks().map(t => ({
        kind: t.kind,
        enabled: t.enabled,
        readyState: t.readyState
      })),
      peerConnectionState: this.peerConnection?.connectionState,
      iceConnectionState: this.peerConnection?.iceConnectionState,
      remoteAudioPlaying: this.remoteAudioElement && !this.remoteAudioElement.paused,
      remoteAudioSrc: this.remoteAudioElement?.srcObject ? 'has stream' : 'no stream'
    };
  }

  // ==========================================
  // STATIC HELPERS
  // ==========================================

  static isSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      window.RTCPeerConnection
    );
  }

  static async getAvailableDevices(): Promise<{
    audioInputs: MediaDeviceInfo[];
    audioOutputs: MediaDeviceInfo[];
  }> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      return {
        audioInputs: devices.filter(device => device.kind === 'audioinput'),
        audioOutputs: devices.filter(device => device.kind === 'audiooutput')
      };
    } catch (error) {
      console.error('Error getting devices:', error);
      return {
        audioInputs: [],
        audioOutputs: []
      };
    }
  }
}

// Create singleton instance
export const webrtcService = new WebRTCService();

// Export the class for static method access
export { WebRTCService };

// Add to window for debugging
declare global {
  interface Window {
    webrtcService: WebRTCService;
    debugWebRTC: () => void;
  }
}

if (typeof window !== 'undefined') {
  window.webrtcService = webrtcService;
  
  // Debug helper function
  window.debugWebRTC = () => {
    const info = webrtcService.getDebugInfo();
    console.log('=== WebRTC Debug Info ===');
    console.table(info);
    console.log('Local stream tracks:', info.localStreamTracks);
    console.log('======================');
    return info;
  };
  
  console.log('💡 [WebRTC] Debug tip: Run window.debugWebRTC() in console to see call status');
}





